// Description: Render a Dynamic Input that will appear in the Dynamic Form
// Updated: 24/09/2025
// Author: Simon Franklin

// React
import React, { useEffect, useState } from "react";

// Material UI Components
import {
  Box,
  Button,
  createTheme,
  FormControl,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import LockIcon from "@mui/icons-material/Lock";

// Components and functions for the project
import ptLocale from "date-fns/locale/pt-BR";
import dayjs from "dayjs";
import themeColors from "../ThemeColors/ThemeColors";
import StatusIcon from "../StatusLabel/StatusIcon";
import { useNavigate } from "react-router-dom";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_TZ = "America/Sao_Paulo";
dayjs.tz.setDefault(DEFAULT_TZ);

// Render The Component
const DynamicInput = ({
  title,
  value,
  type,
  fullWidth,
  updateFieldValue,
  formState,
  options = null,
  tooltip,
  error,
  setError,
  setSelectedTemplate
}) => {

  const [minTime, setMinTime] = useState(null);
  const [hover, setHover] = useState(false);

  const iconColor = hover === true ? error[title] === true ? themeColors.red03 : themeColors.purple500 : themeColors.gray30;
  const navigate = useNavigate();

  const handleTimeChange = (value, field) => {
    if (value && value < minTime) {
      updateFieldValue(field, value);
    } else {
      updateFieldValue(field, value);
    }
  };

  useEffect(() => {
    const now = new Date();
    const startDay = new Date(formState["Data de início"]);

    if (
      startDay.getFullYear() > now.getFullYear() ||
      startDay.getMonth() > now.getMonth() ||
      startDay.getDate() > now.getDate()
    ) {
      setMinTime(null);
    } else {
      const minimumTime = new Date(now.getTime() + 65 * 60000);

      minimumTime.setMinutes(0);
      minimumTime.setSeconds(0);
      minimumTime.setMilliseconds(0);

      setMinTime(minimumTime);
    }
  }, [formState["Data de início"]]);

  const startDateTheme = createTheme({
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: error[title] === true ? themeColors.red01 : '#FFF',
            color: themeColors.gray30,
            borderRadius: "6px",
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            color: themeColors.purple400,
            borderRadius: "2px",
            borderWidth: "1px",
            border: "1px solid",
            backgroundColor: "#FFF",
            "&:hover": {
              backgroundColor: themeColors.purple400,
              color: "#FFF",
            },
            "&.Mui-selected": {
              backgroundColor: themeColors.purple400 + " !important",
              color: "#FFF !important",
            },
            "&.css-nxzh5e-MuiFormControl-root-MuiTextField-root": {
              minWidth: "100%"
            }
          },
        },
      },
    },
  });

  const startTimeTheme = createTheme({
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: error[title] === true ? themeColors.red01 : '#FFF',
            borderRadius: "6px",
            color: themeColors.gray30,
          },
        },
      },
      MuiPickersClock: {
        styleOverrides: {
          clock: {
            backgroundColor: "#FFF",
            borderRadius: "12px",
            border: "1px solid #DBDBDB",
            padding: "10px",
          },
          pin: {
            backgroundColor: themeColors.purple400,
          },
        },
      },
    },
  });

  const renderInput = () => {
    switch (type) {
      case "textarea":
        return (
          <TextField
            name={title}
            variant="outlined"
            placeholder={title}
            value={value === false ? "" : value}
            onChange={(e) => {
              updateFieldValue(title, e.target.value);
              if (error[title] === true) {
                setError((prev) => ({
                  ...prev,
                  [title]: false
                }));
              }
            }}
            rows={4}
            multiline
            fullWidth
            sx={{ '& .css-17m5yr0-MuiInputBase-root-MuiOutlinedInput-root': { backgroundColor: error[title] === true ? themeColors.red01 : '#FFF', height: '100% !important' }, borderRadius: '6px', '.css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input': { padding: '13px' } }}
          />
        );
      case "date":
        return (
          <ThemeProvider theme={startDateTheme}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={"pt-br"}
            >
              <DesktopDatePicker
                sx={{
                  '& .css-4x2ipa-MuiFormControl-root-MuiTextField-root': { width: '100%' },
                  '& .css-1a5caln-MuiInputBase-root-MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: 'hsl(220, 20%, 88%)' }
                }}
                name={title}
                inputFormat="DD/MM/YYYY"
                value={value ? dayjs(value) : null}
                onChange={(newValue) => {
                  updateFieldValue(title, newValue.toISOString());
                  if (title === "Data de início") {
                    updateFieldValue("Horário de início", null);
                  };
                  if (error[title] === true) {
                    setError((prev) => ({
                      ...prev,
                      [title]: false
                    }));
                  };
                }}
                minDate={dayjs()}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </ThemeProvider>
        );
      case "time":
        return (
          <ThemeProvider theme={startTimeTheme}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={ptLocale}
            >
              <TimePicker
                sx={{ '.css-xbawzo-MuiFormControl-root-MuiTextField-root': { minWidth: '100%' }, '& .css-1wgkh1q-MuiFormControl-root-MuiTextField-root': { width: '100%' }, '& .css-1gfnrqc-MuiInputBase-root-MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline' : { borderColor: 'hsl(220, 20%, 88%)' } }}
                name={title}
                ampm={false}
                minutesStep={60}
                value={value ? dayjs(value).tz(DEFAULT_TZ).toDate() : null}
                minTime={minTime ? dayjs(minTime).tz(DEFAULT_TZ).toDate() : null}
                onChange={(newValue) => {
                  if (!newValue) {
                    handleTimeChange(null, title);
                    return;
                  }

                  const adjusted = dayjs(newValue).tz(DEFAULT_TZ).toDate();

                  handleTimeChange(adjusted, title);
                  setError((prev) => ({
                    ...prev,
                    [title]: false
                  }))
                }}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </ThemeProvider>
        );
      case "select":
        return (
          <Select
            name={title}
            sx={{
              backgroundColor: error[title] === true ? themeColors.red01 : '#FFF',
              borderRadius: "6px",
              minWidth: "100%",
              ".css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input": {
                padding: "13px",
              },
            }}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "redirect") {
                const findRedirect = options.find((item) => item.id === "redirect");

                if (findRedirect) {
                  sessionStorage.setItem("creating_campaign", true);
                  sessionStorage.setItem("formState", JSON.stringify(formState));
                  navigate(findRedirect.path);
                };

              } else if (title.includes("modelo")) {
                setSelectedTemplate(value);
                updateFieldValue(title, value);
                if (error[title] === true) {
                  setError((prev) => ({
                    ...prev,
                    [title]: false
                  }));
                }
              } else {
                updateFieldValue(title, value);
                if (error[title] === true) {
                  setError((prev) => ({
                    ...prev,
                    [title]: false
                  }));
                }
              }

            }}
            value={value}
          >
            <MenuItem style={{ color: themeColors.gray30 }} value={""} disabled>
              {title}
            </MenuItem>
            {
              options &&
                title === "Adicionar Listagem" ? (
                options.map((option, index) => (
                  <MenuItem key={index} value={option.id}>
                    {(option.title + ': ' + option.desc).substring(0, 30) + '...'}
                  </MenuItem>
                ))
              ) : (
                options.map((option, index) => (
                  <MenuItem key={index} value={option.id}>
                    {option.title}
                  </MenuItem>
                ))
              )
            }
          </Select>
        );
      case "block":
        return (
          <TextField
            placeholder={`${title} (Bloqueado)`}
            id={title}
            variant="outlined"
            fullWidth
            sx={{ background: '#FFF', borderRadius: '6px', '.css-nxo287-MuiInputBase-input-MuiOutlinedInput-input': { padding: '13px' } }}
            autoComplete='off'
            disabled={true}
            InputProps={{
              endAdornment: (
                <LockIcon style={{ color: themeColors.gray30 }} />
              ),
            }}
          />
        );
      case "type":
        return (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              maxWidth: '100%',
              gap: '13.72px',
              justifyContent: 'space-between',
              height: '29px',
              overflowX: 'auto'
            }}
          >
            {options.map((type, index) => (
              <Button
                key={type}
                onClick={() => {
                  updateFieldValue(title, type.title)
                  if (error[title] === true) {
                    setError((prev) => ({
                      ...prev,
                      [title]: false
                    }));
                  }
                }}
                sx={{
                  flexGrow: 1,
                  mx: index === 0 || index === options.length - 1 ? 0 : 0.5,
                  bgcolor:
                    formState[title] === type.title ? '#8142FC' : error[title] === true ? themeColors.red01 : themeColors.gray30,
                  color: error[title] === true ? themeColors.red03 : '#fff',
                  borderRadius: '27px',
                  '&:hover': {
                    bgcolor: formState[title] === type.title ? '#8142FC' : 'gray',
                  },
                  fontSize: '12px',
                  padding: { xs: '4px 10px', sm: '4px 16px' },
                  height: '29px'
                }}
              >
                {type.title}
              </Button>
            ))}
          </Box>
        )
      case "status":
        return (
          <Box sx={{ display: 'flex', gap: '6px', flexDirection: 'column', width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40px', backgroundColor: formState["Status da Campanha"] === 'Active' || formState["Status da Campanha"] === 'active' ? themeColors.green01 : themeColors.red01, width: '100%', borderRadius: '6px' }}>
              <Typography sx={{ color: formState["Status da Campanha"] === 'Active' || formState["Status da Campanha"] === 'active' ? themeColors.green03 : themeColors.red03, fontSize: '16px', fontWeight: 500 }}>{formState["Status da Campanha"] === 'Active' || formState["Status da Campanha"] === 'active' ? 'Ativa' : 'Desativada'}</Typography>
            </Box>
          </Box>
        )
      default:
        return (
          <TextField
            name={title}
            variant="outlined"
            placeholder={title}
            value={value === false ? "" : value}
            onChange={(e) => {
              updateFieldValue(title, e.target.value);
              if (error[title] === true) {
                setError((prev) => ({
                  ...prev,
                  [title]: false
                }));
              }
            }}
            fullWidth
            rows={1}
            sx={{ '& .css-1d0qoyg-MuiInputBase-root-MuiOutlinedInput-root': { backgroundColor: error[title] === true ? themeColors.red01 : '#FFF' }, borderRadius: '6px', '.css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input': { padding: '13px' } }}
          />
        );
    }
  };

  return (
    <FormControl sx={{ width: fullWidth ? "100%" : "calc(50% - 11.5px)", display: "flex", flexDirection: "column", gap: "10px" }} key={title}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", color: error[title] === true ? "red" : "#000", fontSize: "14px" }}>
        {title}
        {
          tooltip !== "" && (
            <Tooltip title={
              <Typography variant="body2">
                {tooltip}
              </Typography>
            }
              placement="top"
            >
              <span onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={{ display: "inline-block" }}>
                <StatusIcon status={"Alert"} color={iconColor} />
              </span>
            </Tooltip>
          )
        }
      </div>
      {renderInput()}
    </FormControl>
  );
};

export default DynamicInput;